<div class="flex flex-col gap-3">
  <!-- Where the record stands, and how much of it is settled. -->
  <section class="flex flex-wrap items-center gap-1.5">
    <guarantees-chip
      [tone]="statusTone()"
      [tooltip]="statusHintKey() | translate"
      >{{ statusLabelKey() | translate }}</guarantees-chip
    >
    <span class="inline-flex items-center gap-1 text-sm text-neutral-500">
      <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">{{ categoryIcon() }}</mat-icon>
      {{ categoryLabelKey() | translate }}
    </span>

    <guarantees-prescription-stats
      class="ml-auto"
      [summary]="visit().summary"
      [fulfillment]="visit().fulfillmentSummary"
    ></guarantees-prescription-stats>
  </section>

  <!-- Above the record itself: the deletion is the first thing true of it, and the reason is here. -->
  @if (visit().isDeleted) {
    <guarantees-callout
      tone="error"
      stack
    >
      <span class="inline-flex items-center gap-1 text-sm font-semibold text-error-700">
        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">delete</mat-icon>
        {{ "guaranteeV2.label.deleted" | translate }}
        @if (visit().deletedAt; as deletedAt) {
          <span class="font-normal tabular-nums">{{ deletedAt | date: "dd.MM.yy HH:mm" }}</span>
        }
        @if (deletedByName(); as name) {
          <span class="font-normal">&middot; {{ name }}</span>
        }
      </span>
      @if (visit().deletionReason; as reason) {
        <span class="text-sm text-error-700">{{ reason }}</span>
      }
    </guarantees-callout>
  }

  <!-- The record: who raised it, who last touched it, and under which contract. -->
  <guarantees-panel-section [label]="'guaranteeV2.label.guarantee' | translate">
    <div class="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-x-4 gap-y-2">
      <guarantees-fact
        [label]="'guaranteeV2.label.createdAt' | translate"
        [hint]="creatorHintKey() | translate"
      >
        {{ visit().createdAt | date: "dd.MM.yy HH:mm" }}
      </guarantees-fact>

      <guarantees-fact
        [label]="'guaranteeV2.label.createdBy' | translate"
        [hint]="creatorHintKey() | translate"
      >
        {{ createdByName() || "—" }}
      </guarantees-fact>

      @if (visit().updatedAt; as updatedAt) {
        <guarantees-fact [label]="'guaranteeV2.label.updatedAt' | translate">{{ updatedAt | date: "dd.MM.yy HH:mm" }}</guarantees-fact>
        <guarantees-fact [label]="'guaranteeV2.label.updatedBy' | translate">{{ updatedByName() || "—" }}</guarantees-fact>
      }

      <guarantees-fact [label]="'guaranteeV2.label.contractNumber' | translate">{{ visit().contractNumber || "—" }}</guarantees-fact>
    </div>

    @if (visit().description; as description) {
      <guarantees-callout>{{ description }}</guarantees-callout>
    }
  </guarantees-panel-section>

  <!-- The visit behind it. A guarantee raised without one says so rather than showing blank fields. -->
  <guarantees-panel-section [label]="'guaranteeV2.label.visit' | translate">
    <!-- A guarantee raised without a visit still names its partner, so the facts stand either way. -->
    @if (!appointmentId()) {
      <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.withoutAppointmentHint" | translate }}</span>
    }

    <div class="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-x-4 gap-y-2">
      @if (appointmentId()) {
        <guarantees-fact
          [label]="'guaranteeV2.label.appointmentDate' | translate"
          [hint]="visit().hasAppointmentDate ? '' : ('guaranteeV2.message.noAppointmentDateHint' | translate)"
        >
          {{ visit().hasAppointmentDate ? (visit().date | date: "dd.MM.yy HH:mm") : "—" }}
        </guarantees-fact>
      }
      <guarantees-fact [label]="'guaranteeV2.label.partner' | translate">{{ visit().partnerName || "—" }}</guarantees-fact>
      <guarantees-fact
        [label]="'guaranteeV2.label.doctor' | translate"
        [hint]="visit().creatorType === creatorTypes.Doctor ? (creatorHintKey() | translate) : ''"
        >{{ visit().doctorName || "—" }}</guarantees-fact
      >
    </div>

    @if (appointmentId()) {
      @if (sharedVisitCount(); as count) {
        <span class="inline-flex items-center gap-1 text-sm text-neutral-500">
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">call_merge</mat-icon>
          {{ "guaranteeV2.message.sharedVisitHint" | translate: { count } }}
        </span>
      }

      <!--
        Where this visit came from: the line of an earlier guarantee that booked it. The name is
        stated even when the guarantee behind it is gone, which is what the disabled state says.
      -->
      @if (visit().precededByPrescriptionName; as prescriptionName) {
        <button
          class="inline-flex max-w-full items-center gap-1 self-start rounded text-sm font-semibold text-primary-600 hover:underline focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:text-neutral-400 disabled:no-underline"
          type="button"
          [disabled]="!visit().precededByGuaranteeId"
          [matTooltip]="'guaranteeV2.message.bookedByPrescriptionHint' | translate: { name: prescriptionName }"
          (click)="openPrecedingGuarantee()"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">subdirectory_arrow_left</mat-icon>
          <span class="truncate">{{ prescriptionName }}</span>
        </button>
      }
    }
  </guarantees-panel-section>

  @if (diagnoses().length > 0) {
    <guarantees-panel-section [label]="'guaranteeV2.label.guaranteeDiagnoses' | translate">
      <div class="flex flex-col gap-1">
        @for (diagnosis of diagnoses(); track diagnosis.id) {
          <guarantees-diagnosis-chip
            [diagnosis]="diagnosis"
            [showCaseState]="false"
          ></guarantees-diagnosis-chip>
        }
      </div>
    </guarantees-panel-section>
  }

  <!-- Why the record reads the way it does: every note its rulings and amendments were given with. -->
  @if (notes().length > 0) {
    <guarantees-panel-section [label]="'guaranteeV2.label.decisionNotes' | translate">
      <div class="flex flex-col gap-1">
        @for (note of notes(); track note.note.key) {
          <guarantees-recorded-note
            [note]="note.note"
            [prescriptionName]="note.prescriptionName"
          ></guarantees-recorded-note>
        }
      </div>
    </guarantees-panel-section>
  }

  <guarantees-record-breaches [breaches]="breaches()"></guarantees-record-breaches>

  <!-- Everything done to the record since it was raised, read where the record is read. -->
  <guarantees-change-history
    kind="guarantee"
    [recordId]="visit().guaranteeId"
  ></guarantees-change-history>
</div>
